import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";

// Defense in depth: (1) DOMPurify allow-list + forbid-list, (2) sandboxed iframe
// WITHOUT allow-scripts so nothing can execute even if a vector slipped through,
// (3) a restrictive CSP inside the frame document.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    "table", "tbody", "thead", "tr", "td", "th",
    "div", "span", "p", "br", "hr",
    "a", "img",
    "strong", "b", "em", "i", "u",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "ul", "ol", "li",
    "font", "small",
  ],
  ALLOWED_ATTR: [
    "style", "class", "href", "target", "rel",
    "src", "alt", "width", "height", "referrerpolicy",
    "cellpadding", "cellspacing", "border", "valign", "align",
    "bgcolor", "color", "face", "size",
  ],
  ALLOW_DATA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  RETURN_TRUSTED_TYPE: false,
  SAFE_FOR_TEMPLATES: true,
  FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "input", "textarea", "button", "style", "link", "meta"],
  FORBID_ATTR: [
    "onerror", "onload", "onclick", "onmouseover", "onmouseout", "onmousedown",
    "onmouseup", "onfocus", "onblur", "onchange", "onsubmit", "onkeydown",
    "onkeyup", "onkeypress", "onabort", "oncontextmenu", "ondrag", "ondrop",
    "onanimationstart", "onanimationend", "ontransitionend",
    "srcdoc", "formaction",
  ],
};

const FRAME_CSP = "default-src 'none'; img-src * data:; style-src 'unsafe-inline'; font-src * data:";
// allow-same-origin (without allow-scripts) only lets the parent measure height.
const FRAME_SANDBOX = "allow-same-origin allow-popups allow-popups-to-escape-sandbox";
const MIN_HEIGHT = 188;

const frameDocument = (body) => `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${FRAME_CSP}">
<base target="_blank">
<style>html,body{margin:0;padding:0;background:#fff;font-family:Arial,Helvetica,sans-serif;color:#0f172a}</style>
</head><body>${body}</body></html>`;

export function SignaturePreviewFrame({ html, mode = "desktop", testId }) {
  const width = mode === "mobile" ? 360 : 640;
  const frameRef = useRef(null);
  const observerRef = useRef(null);
  const [height, setHeight] = useState(MIN_HEIGHT);
  const srcDoc = useMemo(() => frameDocument(DOMPurify.sanitize(html || "", SANITIZE_CONFIG)), [html]);

  // body (not documentElement) so the frame can also shrink; observer handles reflow on resize.
  const fitToContent = useCallback(() => {
    const body = frameRef.current?.contentDocument?.body;
    if (!body) return;
    const measure = () => setHeight(Math.max(MIN_HEIGHT, Math.ceil(body.getBoundingClientRect().height)));
    measure();
    observerRef.current?.disconnect();
    observerRef.current = new ResizeObserver(measure);
    observerRef.current.observe(body);
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden" data-testid={testId}>
      <div className="bg-slate-100 border-b border-slate-200 px-3 py-1.5 flex items-center justify-between text-[11px] text-slate-500 uppercase tracking-wider">
        <span>{mode === "mobile" ? "Vista Mobile" : "Vista Desktop"}</span>
        <span>{width}px</span>
      </div>
      <div className="p-4 bg-white overflow-auto" style={{ minHeight: 220 }}>
        <iframe
          ref={frameRef}
          title={mode === "mobile" ? "Vista previa mobile de la firma" : "Vista previa desktop de la firma"}
          sandbox={FRAME_SANDBOX}
          srcDoc={srcDoc}
          onLoad={fitToContent}
          style={{ width, maxWidth: "100%", height, border: 0, display: "block" }}
          data-testid={testId ? `${testId}-frame` : undefined}
        />
      </div>
    </div>
  );
}
